import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Badge from '../../../../components/Badge';
import { Image } from '../../../../components/image/Image';
import { ProgramSource, Source, VodSource } from '../../../../queries/ats/titles/types/sources';
import { getPreferredRecording } from '../../../../queries/ats/titles/utils/recording-utils';
import css from './episode.module.scss';

interface EpisodeBadgeProps {
  source: Source;
}

export function EpisodeBadge({ source }: EpisodeBadgeProps) {
  switch (source.type) {
    case 'program':
      return <EpisodeProgramBadge source={source} />;
    case 'vod':
      return <EpisodeVodBadge source={source} />;
    default:
      return <Image className={css.logo} src={source.image} alt="provider logo" loading="lazy" />;
  }
}

interface EpisodeProgramBadgeProps {
  source: ProgramSource;
}

function EpisodeProgramBadge({ source }: EpisodeProgramBadgeProps) {
  const { t } = useTranslation();

  const recording = getPreferredRecording(source, true);

  if (recording !== undefined) {
    switch (recording.status) {
      case 'ongoing':
        return <Badge className={css.badge} text={t('common:recording')} type="primary" />;
      case 'completed':
        return <Badge className={css.badge} text={t('common:recorded')} type="light" />;
    }
  }

  const expiration = moment(source.expiresAt);
  const now = moment();

  if (now.isAfter(expiration)) {
    return <Badge className={css.badge} text={t('common:expired')} type="dark" />;
  }

  return <Image className={css.logo} src={source.image} alt="provider logo" loading="lazy" />;
}

interface EpisodeVodBadgeProps {
  source: VodSource;
}

function EpisodeVodBadge({ source }: EpisodeVodBadgeProps) {
  const { t } = useTranslation();

  switch (source.product.status) {
    case 'pending':
      return (
        <Badge
          className={css.badge}
          // TODO: Temp fix for gjermund & co to test cvp.

          text={t('common:price', { value: source.product.price?.amount ?? '???' })}
          type="dark"
        />
      );
    case 'purchased':
      return <Badge className={css.badge} text={t('common:purchased')} type="success" />;
    case 'rented':
      return (
        <Badge
          className={css.badge}
          text={`${t('common:expires')} ${moment(source.product.expiresAt).fromNow()}`}
          type="dark"
        />
      );
  }
}
